/** Narrow view contracts. Backend assets and source versions remain authoritative. */
export interface ReleaseAsset {
  id: string;
  kind?: string;
  mime_type?: string;
  created_at?: string;
  job_id?: string;
  metadata?: {
    width?: number;
    height?: number;
    resolution?: string;
    duration_ms?: number;
    fps?: number;
    burn_subtitles?: boolean | null;
    source_output_id?: string;
    source_sha256?: string;
    start_ms?: number;
    end_ms?: number;
    fit?: "contain" | "cover";
    crop_x?: number;
    transition_pack?: string;
    cover_layout?: string;
  };
}
export interface ReleaseProject {
  id: string;
  title?: string;
  artist?: string;
  outputs?: ReleaseAsset[];
  assets?: ReleaseAsset[];
  scenes?: { id: string; position: number; image_asset_id?: string }[];
  jobs?: ReleaseJob[];
}
export interface ReleaseJob {
  id: string;
  kind: string;
  status: string;
  created_at?: string;
  progress?: number;
  current_step?: string;
  user_error?: string;
  request?: { short_export?: ShortExportRequest };
}
export interface ShortMaster {
  output_id: string;
  sha256: string;
  duration_ms: number;
  width: number;
  height: number;
  fps: number;
  has_audio: boolean;
  burn_subtitles: boolean | null;
}
export interface ShortExportRequest {
  source_output_id: string;
  source_sha256: string;
  start_ms: number;
  end_ms: number;
  fit: "contain" | "cover";
  crop_x: number;
  crop_confirmed: boolean;
}
export interface ShortExports {
  loadMaster: (outputId: string) => Promise<ShortMaster>;
  start: (request: ShortExportRequest) => Promise<void>;
  stop: (jobId: string) => Promise<void>;
}
export interface CoverDraft {
  source_scene_id: string;
  source_asset_id: string;
  cover_layout: "clean" | "editorial" | "poster";
  title: string;
  subtitle: string;
  show_text: boolean;
  crop_x: number;
}
export interface ShortDraft {
  start: number;
  end: number;
  fit: "contain" | "cover";
  x: number;
}
export type ReleaseTab = "film" | "cover" | "shorts";
export type ResolveReleaseUrl = (asset?: ReleaseAsset) => string;
export const releaseDrafts = {
  covers: new Map<string, CoverDraft>(),
  shorts: new Map<string, ShortDraft>(),
  tabs: new Map<string, ReleaseTab>(),
  pendingCovers: new Set<string>(),
};
// Iris can ask an open studio for a tab (the Shorts lab after a shorts goal); a closed one opens on it.
const tabListeners = new Set<(projectId: string, tab: ReleaseTab) => void>();
export function requestReleaseTab(projectId: string, tab: ReleaseTab) {
  releaseDrafts.tabs.set(projectId, tab);
  tabListeners.forEach((listener) => listener(projectId, tab));
}
export function onReleaseTabRequest(listener: (projectId: string, tab: ReleaseTab) => void) {
  tabListeners.add(listener);
  return () => {
    tabListeners.delete(listener);
  };
}
const pendingListeners = new Set<() => void>();
export function subscribeCoverPending(listener: () => void) {
  pendingListeners.add(listener);
  return () => {
    pendingListeners.delete(listener);
  };
}
export function setCoverPending(projectId: string, pending: boolean) {
  if (pending) releaseDrafts.pendingCovers.add(projectId);
  else releaseDrafts.pendingCovers.delete(projectId);
  pendingListeners.forEach((listener) => listener());
}
export const releaseStamp = (date?: string) => Date.parse(date || "") || 0;
export function sortReleaseItems<T extends { created_at?: string }>(
  items: T[] = [],
): T[] {
  return [...items].sort(
    (a, b) => releaseStamp(b.created_at) - releaseStamp(a.created_at),
  );
}
export function releaseUrl(
  asset: ReleaseAsset | undefined,
  resolve: ResolveReleaseUrl,
): string {
  if (!asset) return "";
  const candidate = resolve(asset) || "";
  if (/[\\\u0000-\u0020]/.test(candidate)) return "";
  if (candidate.startsWith("/") && !candidate.startsWith("//"))
    return candidate;
  try {
    const parsed = new URL(candidate);
    return ["http:", "https:"].includes(parsed.protocol) &&
      !parsed.username &&
      !parsed.password
      ? candidate
      : "";
  } catch {
    return "";
  }
}
export function currentCoverFrames(project: ReleaseProject) {
  return (project.scenes || []).flatMap((scene) => {
    const asset = project.assets?.find(
      (item) =>
        item.id === scene.image_asset_id &&
        item.mime_type?.startsWith("image/"),
    );
    return asset ? [{ scene, asset }] : [];
  });
}
